import { defineCopy } from '@floorfile/i18n'

/**
 * Nonconformances — the material-control record (localization plan, batch 5b).
 *
 * ## Almost every enum in this slice was reaching the screen raw
 *
 * `NcrStatus`, `NcrSeverity`, `NcrDispositionType` and `NcrSourceType` — all
 * four — were rendered as their contract tokens: `<StatusBadge label={n.status}>`,
 * `<b>{d.type}</b>`, `<dd>{n.sourceType}</dd>`, and three `<select>`s whose
 * `<option>`s were the bare codes. The lint rule sees none of it (a property
 * access is not a literal; an `<option>` child that is an identifier is not
 * either), which is why a slice with only 13 flagged literals on its list page
 * had four unlabelled enums on it.
 *
 * **Two builders for the same four words had already drifted.**
 * `EditNcrDialog`'s severity `<select>` said `Minor` / `Major` / `Critical`
 * while `RaiseNcrButton`'s said `MINOR` / `MAJOR` / `CRITICAL` and the badge on
 * the register said `MINOR`. Three surfaces, two spellings, one enum. They all
 * read `severity` now.
 *
 * **English changes** on status, severity, disposition type and source. Checked
 * against `apps/e2e` first — see the note on `materialHold` below, which is the
 * one string that must not move.
 *
 * ## `Material hold` stopped shouting (owner decision, 2026-09-27 — FF-QA-014/072)
 *
 * `ncr.spec.ts` asserts `getByText('Material hold')` three times — it is how
 * the spec proves a hold was placed and then released, so the English is still
 * byte-locked; the warning tone carries the urgency, the case no longer does.
 *
 * `Place hold` and `Release hold` are also `getByRole` names in that spec.
 *
 * ## `$` is a placeholder, not a prefix
 *
 * Six sites wrote `` `$${Number(x).toFixed(2)}` ``. fr-CA writes `12,50 $` —
 * symbol after the number, with a space. One `amount` message, the same
 * decision `downtime` made for `stats.amount`, and for the same reason: a bare
 * `'$'` glued on in JSX can only ever be on the wrong side somewhere.
 */
export const ncrCopy = defineCopy('ncr', {
  offlineAction: { raise: 'Raise NCR' },
  /** `NcrSourceType` — what raised this NCR. Stored lowercase. */
  source: {
    check: 'Check',
    incoming_inspection: 'Incoming inspection',
    incident: 'Incident',
    audit_finding: 'Audit finding',
    complaint: 'Customer complaint',
    deviation: 'Deviation',
    oos: 'OOS investigation',
    manual: 'Manual',
  },

  /** A currency amount. Symbol position is the translator's — see the header. */
  amount: '${amount}',

  raise: {
    button: 'Raise NCR',
    /** FF-QA-018: the register header's "+ New <noun>" — the escalation buttons on a check or an incident keep the verb. */
    newAction: '+ New NCR',
    title: 'Raise nonconformance',
    fieldTitle: 'Title',
    description: 'Description',
    quantity: 'Quantity',
    severity: 'Severity',
    affectedLot: 'Affected lot',
    cancel: 'Cancel',
    submit: 'Raise NCR',
    raising: 'Raising…',
    /** FF-AUDIT-099: in-app field answers and the success note. */
    required: 'Required.',
    quantityPositive: 'Enter a quantity above zero.',
    raised: '{number} raised.',
  },

  edit: {
    title: 'Edit {number}',
    fieldTitle: 'Title',
    description: 'Description',
    severity: 'Severity',
    productRef: 'Product reference',
    /** FF-AUDIT-074: editable while open; the server keeps it at or above what was released. */
    quantityAffected: 'Affected quantity',
    unit: 'Unit',
  },

  list: {
    /* Renamed from 'Nonconformances' — owner decision 2026-08-14, the last of
       the P1 shortlist and the one held back for a field-user call. The choice
       was between `Quality issues (NCRs)` and `Nonconformance reports (NCRs)`;
       the owner picked the plain label with the regulated acronym retained in
       parentheses, so the record is still findable by its formal name.
       ⚠ NOT renamed, deliberately: the route `/quality/nonconformances`, the
       API path `/api/v1/nonconformances`, the `?tab=` slug, and the `NCR`
       column header. Those are addresses and identifiers, not labels. */
    title: 'Quality issues (NCRs)',
    description: 'Contain material, approve disposition, and preserve the complete quality record.',
    tabs: 'Quality issue views',
    /** Keyed by the tab's own id — the id is also the `?tab=` slug, so it is an
     * address and stays English. Only the VALUE is the label. */
    tab: {
      Nonconformances: 'Quality issues',
      'Defect codes': 'Defect codes',
    },
    statusFilter: 'Filter by status',
    detectedFrom: 'Detected from',
    detectedTo: 'Detected to',
    allStatuses: 'All statuses',
    search: 'Search NCRs',
    searchPlaceholder: 'Search NCRs',
    emptyTitle: 'No quality issues match these filters',
    region: 'Quality issues',
    /** Lowercase: read inside "Sorted by {field}, ascending". One entry per
     * field in `NcrSort`, not per header — `sort-announcements.test.ts`
     * enforces it against the contract. */
    sortField: {
      ncNumber: 'number',
      title: 'title',
      severity: 'severity',
      status: 'status',
      detectedAt: 'detected',
    },
    column: {
      number: 'Number',
      title: 'Title',
      facility: 'Facility',
      severity: 'Severity',
      quantity: 'Quantity',
      cost: 'Cost',
      status: 'Status',
      detected: 'Detected',
    },
    /** `{quantity} {unit}` — the unit is a stored code (`EA`), not a word. */
    quantity: '{quantity} {unit}',
    /**
     * The register's hold badge. FF-QA-014: it shouted `ON HOLD` beside
     * Title-case pills; the header's `Material hold` (`detail.materialHold`)
     * is the one `ncr.spec.ts` asserts.
     */
    onHold: 'On hold',
  },

  detail: {
    notFound: 'Nonconformance not found.',
    loading: 'Loading…',
    back: '← Back to quality issues',
    /** Byte-locked in English — see `list.onHold` and the header. */
    materialHold: 'Material hold',
    editDetails: 'Edit details',
    evidenceBundle: 'Evidence bundle',
    /** FF-AUDIT-095: lifecycle exits on the record. */
    cancelNcr: 'Cancel NCR…',
    archive: 'Archive',
    restore: 'Restore',
    cancelledLabel: 'Cancelled:',
    archivedOn: 'Archived {date}',
    cancelDialog: {
      title: 'Cancel {number}?',
      description: 'Cancellation is a recorded outcome, not a deletion — the NCR leaves the open counts and the cost-of-quality reports, and the reason stays on the record and in the audit log.',
      reason: 'Reason (required)',
      keep: 'Keep NCR',
      confirm: 'Cancel NCR',
      cancelling: 'Cancelling…',
    },
    materialControl: 'Material control',
    /** One message: the two quantities and the separator are a sentence. */
    affected: 'Affected: {quantity} {unit} · released remainder: {released}',
    holdNote: 'Hold/release note',
    placeHold: 'Place hold',
    releaseHold: 'Release hold',
    mrbDisposition: 'MRB disposition',
    /** One recorded disposition: "Rework · 12 EA". */
    dispositionLine: '{type} · {quantity} {unit}',
    dispositionType: 'Disposition type',
    dispositionQuantity: 'Disposition quantity',
    quantityPlaceholder: 'Quantity ({unit})',
    dispositionRationale: 'Disposition rationale',
    approve: 'Approve full disposition',
    signAndApprove: 'Sign & approve full disposition',
    /** FF-AUDIT-083: the prerequisites are checked before the signature dialog opens; each sentence names the shortfall. */
    precheck: {
      notOnHold: 'Place the affected material on hold before disposition — use "Place hold" above.',
      noQuantity: 'Enter the quantity this disposition covers.',
      exceeds: 'The quantity exceeds the {affected} {unit} affected.',
      remainder: '{remaining} {unit} of the {affected} {unit} affected are not covered. Add them to this disposition, or release the remainder below.',
      releaseRationale: 'State why the remaining {remaining} {unit} are released.',
      noRationale: 'Enter the disposition rationale.',
      coverage: 'Covers {quantity} of {affected} {unit} · {remaining} {unit} remaining',
      releaseRemainder: 'Release the remaining {remaining} {unit}',
      releaseRationaleLabel: 'Release rationale',
      keptValues: 'The disposition was refused. Your entries are kept — correct them and try again.',
      /** FF-QA-071: shown in place of the MRB form to the person who raised the NCR (Settings → Policies may allow self-disposition). */
      selfDispositionNote: 'You raised this nonconformance, so someone else must disposition it.',
    },
    evidenceFiles: 'Evidence files',
    attach: 'Attach',
    /** Fiix audit Phase 2: the rail's accessible name and the header overflow's. */
    facts: 'Nonconformance facts',
    moreActions: 'More actions for this nonconformance',
    archivedLabel: 'Archived',
    startReview: 'Start review',
    raiseCar: 'Raise CAR',
    closeNcr: 'Close NCR',
    /** FF-QA-073 */
    closeBlockedByHold: 'Release the hold first.',
    source: 'Source',
    costOfQuality: 'Cost of quality',
    detected: 'Detected',
    correctiveAction: 'Corrective action',
    openCar: 'Open CAR',
  },

  cost: {
    heading: 'Cost of quality',
    editButton: 'Edit costs and defect code',
    scrap: 'Scrap',
    rework: 'Rework',
    other: 'Other',
    total: 'Total',
    /** The read-only defect-code line. */
    defectCodeLabel: 'Defect code: ',
    codesFailed: 'the code list could not be loaded',
    /** `SEAL-MISALIGN — Seal misalignment`. */
    codeLine: '{code} — {name}',
    notClassified: 'not classified — this cost groups under “Unclassified” on the quality Pareto',
    derivedHint: 'The total is the sum of the three components, so it cannot disagree with them.',
    scrapCost: 'Scrap cost',
    reworkCost: 'Rework cost',
    otherCost: 'Other cost',
    defectCode: 'Defect code',
    defectCodeHelp: "Groups this cost on the quality dashboard's Pareto.",
    defectCodeError: 'The defect code list could not be loaded.',
    notClassifiedOption: 'Not classified',
    totalLine: 'Total: {amount}',
    negative: 'Costs cannot be negative.',
    /** FF-AUDIT-072: an amount the form cannot read is refused, never saved as 0. */
    unreadable: 'Enter an amount such as 1250.00 or 1,250.00.',
    willStore: 'Will be stored as {amount}.',
    fixFields: 'Fix the highlighted amounts first.',
    saveFailed: 'Could not save those costs.',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
  },

  defectCodes: {
    whyHeading: 'Why this page exists',
    whyBody:
      'The Quality dashboard’s cost-of-quality Pareto groups by these codes. Until nonconformances carry one, every cost lands under “Unclassified” — which is the truth about the data rather than a clean chart of a fraction of it.',
    addHeading: 'Add a defect code',
    code: 'Code',
    codeHelp: 'Letters, digits, dot, dash and underscore. Stored uppercase.',
    name: 'Name',
    category: 'Category (optional)',
    categoryHelp: "The Pareto's second level, e.g. Machining.",
    description: 'Description (optional)',
    codeRequired: 'Give the code a short identifier, e.g. SEAL-MISALIGN.',
    nameRequired: 'Give the code a name people will recognise.',
    addFailed: 'Could not add that code.',
    add: 'Add code',
    adding: 'Adding…',
    showArchived: 'Show archived codes',
    emptyTitle: 'No defect codes yet',
    emptyDescription: 'Every cost of quality groups under Unclassified until a taxonomy exists.',
    region: 'Defect codes',
    caption: 'Company defect taxonomy',
    column: {
      code: 'Code',
      name: 'Name',
      category: 'Category',
      inUse: 'In use',
      status: 'Status',
      actions: 'Actions',
    },
    archived: 'Archived',
    active: 'Active',
    inactive: 'Inactive',
    unclassifiedNote: 'the unattributed-cost bucket',
    deactivate: 'Deactivate',
    activate: 'Activate',
    restore: 'Restore',
    archive: 'Archive',
  },
})

export type NcrCopy = typeof ncrCopy.en
